import { useEffect, useRef } from 'react';

const DEFAULT_WEBHOOK_URL =
  'https://n8n.srv1057042.hstgr.cloud/webhook/3781eefe-20d0-42a3-b548-5a231bde8018/chat';
const DEFAULT_INSTANCE_ID =
  '8c72f787732ccb349f5c313732519fa9a57ae4bdcfaae0def58e6a631c9386cf';
const CHAT_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/@n8n/chat/dist/chat.bundle.umd.js';
const CHAT_STYLE_URL = 'https://cdn.jsdelivr.net/npm/@n8n/chat/dist/style.css';
const CHAT_STYLE_ID = 'n8n-chat-stylesheet';
const CHAT_SCRIPT_ID = 'n8n-chat-script';
const CHAT_THEME_STYLE_ID = 'n8n-chat-theme';

const ensureStyleSheet = () => {
  if (document.getElementById(CHAT_STYLE_ID)) {
    return;
  }

  const link = document.createElement('link');
  link.id = CHAT_STYLE_ID;
  link.rel = 'stylesheet';
  link.href = CHAT_STYLE_URL;
  document.head.appendChild(link);
};

const ensureThemeOverrides = () => {
    if (document.getElementById(CHAT_THEME_STYLE_ID)) {
      return;
    }

  const style = document.createElement('style');
  style.id = CHAT_THEME_STYLE_ID;
  style.textContent = `
    :root {
      --chat--color--primary: #FFD700;
      --chat--color--primary-shade-50: #e6c200;
      --chat--color--primary--shade-100: #cca900;
      --chat--color--secondary: #0A192F;
      --chat--color-secondary-shade-50: #071326;
      --chat--color-white: #FFFFFF;
      --chat--color-light: #F2F2F2;
      --chat--color-dark: #0A192F;
      --chat--toggle--background: #0A192F;
      --chat--toggle--hover--background: #112a4c;
      --chat--toggle--active--background: #061124;
      --chat--window--width: min(360px, calc(100vw - 1.5rem));
      --chat--window--height: min(560px, calc(100vh - 1.5rem));
      --chat--window--bottom: 1.5rem;
      --chat--window--right: 1.5rem;
      --chat--window--border-radius: 1rem;
      --chat--header--background: #0A192F;
      --chat--header--color: #FFFFFF;
      --chat--button--background: #0A192F;
      --chat--button--hover--background: #112a4c;
    }

    @media (max-width: 640px) {
      :root {
        --chat--window--width: calc(100vw - 1rem);
        --chat--window--height: calc(100vh - 1rem);
        --chat--window--bottom: 0.75rem;
        --chat--window--right: 0.75rem;
      --chat--toggle--size: 56px;
      }
    }
  `;

  document.head.appendChild(style);
};

const loadChatScript = () =>
  new Promise((resolve, reject) => {
    if (window.N8nChat?.createChat) {
      resolve(window.N8nChat);
      return;
    }

    const existingScript = document.getElementById(CHAT_SCRIPT_ID);
    if (existingScript) {
      if (existingScript.getAttribute('data-loaded') === 'true') {
        resolve(window.N8nChat);
        return;
      }

      existingScript.addEventListener('load', () => resolve(window.N8nChat));
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = CHAT_SCRIPT_ID;
    script.src = CHAT_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      resolve(window.N8nChat);
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

const ChatWidget = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const webhookUrl =
      process.env.REACT_APP_N8N_CHAT_WEBHOOK?.trim() || DEFAULT_WEBHOOK_URL;
    const instanceId =
      process.env.REACT_APP_N8N_INSTANCE_ID?.trim() || DEFAULT_INSTANCE_ID;

    if (!webhookUrl || !containerRef.current) {
      console.warn('[ChatWidget] Missing webhook URL or container.');
      return;
    }

    ensureStyleSheet();
    ensureThemeOverrides();

    let chatApp;
    let isCancelled = false;
    let brandingObserver;

    const removeBranding = () => {
      const container = containerRef.current;
      if (!container) {
        return false;
      }

      const brandingWrapper = container.querySelector('.chat-powered-by');
      if (brandingWrapper) {
        brandingWrapper.remove();
        return true;
      }

      return false;
    };

    const observeBranding = () => {
      const container = containerRef.current;
      if (!container || brandingObserver) {
        return;
      }

      brandingObserver = new MutationObserver(() => {
        if (removeBranding()) {
          brandingObserver?.disconnect();
        }
      });

      brandingObserver.observe(container, {
        childList: true,
        subtree: true,
      });
    };

    loadChatScript()
      .then((library) => {
        if (isCancelled) {
          return;
        }

        if (!library?.createChat) {
          throw new Error('n8n chat library did not expose createChat.');
        }

        chatApp = library.createChat({
          target: containerRef.current,
          mode: 'window',
          webhookUrl,
          loadPreviousSession: false,
          showWelcomeScreen: true,
          initialMessages: ['Hola, en que puedo ayudarte?'],
          enableStreaming: false,
          allowFileUploads: false,
          i18n: {
            en: {
              title: 'Hola',
              subtitle: 'Estoy aqui para ayudarte con tu proyecto.',
              getStarted: 'Nueva conversacion',
              inputPlaceholder: 'Escribe tu mensaje...',
            },
          },
          webhookConfig: {
            headers: {
              'Content-Type': 'application/json',
              'X-Instance-Id': instanceId,
            },
          },
        });

        if (!removeBranding()) {
          observeBranding();
        }
      })
      .catch((error) => {
        console.error('[ChatWidget] Failed to load chat widget', error);
      });

    return () => {
      isCancelled = true;
      chatApp?.unmount?.();
      brandingObserver?.disconnect();
    };
  }, []);

  return <div ref={containerRef} className="n8n-chat-container" />;
};

export default ChatWidget;

